import { useCallback, useState } from "react";
import { useMutation, useQuery } from "urql";
import {
  AdminInterviewDetailQuery,
  AdminInterviewForSummaryCheckQuery,
} from "../graphql/interviews.graphql";
import {
  BulkSaveSummaryItemsMutation,
  CompleteSummaryCheckMutation,
  SelfInterviewSummaryItemsQuery,
  UpdateSummaryItemMutation,
  UseCasesQuery,
} from "../graphql/myInterviews.graphql";
import type {
  BulkSaveSummaryItemsInput,
  SummaryItem,
  UpdateSummaryItemInput,
} from "./useMyInterviews";

export function useAdminInterviewGraphQL(interviewId: string | null) {
  const [result, reexecute] = useQuery({
    query: AdminInterviewForSummaryCheckQuery,
    variables: { interviewId: interviewId ?? "" },
    pause: !interviewId,
  });

  return {
    interview: result.data?.interview ?? null,
    loading: result.fetching,
    error: result.error?.message ?? null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export function useAdminInterviewSummaryItemsGraphQL(
  interviewId: string | null,
) {
  const [result, reexecute] = useQuery({
    query: SelfInterviewSummaryItemsQuery,
    variables: { interviewId: interviewId ?? "" },
    pause: !interviewId,
  });

  return {
    summaryItems: result.data?.interviewSummaryItems.nodes ?? [],
    loading: result.fetching,
    error: result.error?.message ?? null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export function useAdminUseCasesGraphQL() {
  const [result, reexecute] = useQuery({ query: UseCasesQuery });

  return {
    useCases: result.data?.useCases.nodes ?? [],
    loading: result.fetching,
    error: result.error?.message ?? null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export function useAdminSummaryCheckMutations() {
  const [, executeUpdateSummaryItem] = useMutation(UpdateSummaryItemMutation);
  const [, executeBulkSaveSummaryItems] = useMutation(
    BulkSaveSummaryItemsMutation,
  );
  const [, executeCompleteSummaryCheck] = useMutation(
    CompleteSummaryCheckMutation,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateSummaryItem = useCallback(
    async (input: UpdateSummaryItemInput): Promise<SummaryItem | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeUpdateSummaryItem({ input });
        if (result.error) {
          throw new Error(result.error.message);
        }
        return result.data?.updateSummaryItem ?? null;
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "要約アイテムの更新に失敗しました";
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeUpdateSummaryItem],
  );

  const bulkSaveSummaryItems = useCallback(
    async (
      input: BulkSaveSummaryItemsInput,
    ): Promise<{
      success: boolean;
      items: SummaryItem[];
      errorMessage: string | null;
    } | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeBulkSaveSummaryItems({ input });
        if (result.error) {
          throw new Error(result.error.message);
        }
        if (!result.data?.bulkSaveSummaryItems) {
          return null;
        }
        return {
          success: result.data.bulkSaveSummaryItems.success,
          items: result.data.bulkSaveSummaryItems.items,
          errorMessage: result.data.bulkSaveSummaryItems.errorMessage ?? null,
        };
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "要約アイテムの一括保存に失敗しました";
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeBulkSaveSummaryItems],
  );

  const completeSummaryCheck = useCallback(
    async (
      interviewId: string,
      satisfactionScore?: number | null,
      behaviorChangeScore?: number | null,
    ): Promise<{
      success: boolean;
      status: string | null;
      summaryCheckCompletedAt: string | null;
      errorMessage: string | null;
    } | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeCompleteSummaryCheck({
          input: {
            interviewId,
            satisfactionScore: satisfactionScore ?? null,
            behaviorChangeScore: behaviorChangeScore ?? null,
          },
        });
        if (result.error) {
          throw new Error(result.error.message);
        }
        if (!result.data?.completeSummaryCheck) {
          return null;
        }
        return {
          success: result.data.completeSummaryCheck.success,
          status: result.data.completeSummaryCheck.status ?? null,
          summaryCheckCompletedAt:
            result.data.completeSummaryCheck.summaryCheckCompletedAt ?? null,
          errorMessage: result.data.completeSummaryCheck.errorMessage ?? null,
        };
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "要約チェックの完了に失敗しました";
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeCompleteSummaryCheck],
  );

  return {
    updateSummaryItem,
    bulkSaveSummaryItems,
    completeSummaryCheck,
    loading,
    error,
  };
}

/** インタビュー詳細（基本情報と録画・文字起こし）。要約は含まない */
export function useAdminInterviewDetailGraphQL(interviewId: string | null) {
  const [result] = useQuery({
    query: AdminInterviewDetailQuery,
    variables: { interviewId: interviewId ?? "" },
    pause: !interviewId,
  });

  return {
    interview: result.data?.interview ?? null,
    loading: result.fetching,
    error: result.error?.message ?? null,
  };
}
